import { ReactComponent as Error } from '@/assets/svgs/icon-error-message-exclamation.svg'
import { Button } from '@dextech-ai/dex-design-system'
import DexLeftComponent from '@/components/DexLeftComponent'
import IconEye from '@/components/IconEye'
import Input from '@/components/Input/index'
import usePage from '@/hooks/use-page'
import useTwoFactorAuthentincation from "@/hooks/use-two-factor-authentication"
import { useNavigate } from '@/lib/router-dom'
import { login } from '@/redux/actions/auth.js'
import { getDexConfig } from '@/utils/get-dex-config'
import { getEnvAndDex } from '@/utils/get-env-and-dex'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

const Page = () => {

	/*=================================================
		General
	/================================================*/

	const dispatch = useDispatch()

	const user = useSelector(state => state.auth.user)
	const environments = useSelector(state => state.auth.environments)

	usePage({ title: 'Activate Two-Factor Authentication' })

	const [customMask, setCustomMask] = useState(true);

	const [lockedAccount, setLockedAccount] = useState()

	/*=================================================
		API
	/================================================*/

	const { goTo, navigate } = useNavigate()
	const [errorMessage, setErrorMessage] = useState()

	const { register, handleSubmit, setError, formState: { errors, isValid } } = useForm({
		mode: 'onChange'
	})

	const { currentItemTwoFactorAuthentication, fetchTwoFactorAuthentication } = useTwoFactorAuthentincation()

	useEffect(() => {
		fetchTwoFactorAuthentication()

		if (localStorage.getItem("lockedTime")) {
			setLockedAccount(localStorage.getItem("lockedTime"))
		}

	}, [])

	const [loading, setLoading] = useState(false);

	/*=================================================
		API Action Functions
	/================================================*/

	const [checkCount, setCheckCount] = useState(0)

	async function formHandler(data) {
		if (loading) {
			return
		}

		setLoading(true)

		const payload = {
			secret: currentItemTwoFactorAuthentication?.secret,
			otp: data.otp
		}

		try {
			await dispatch(login(navigate, payload, "setUpTfa"))

			if (lockedAccount) {
				localStorage.removeItem('lockedTime')
				localStorage.removeItem('lockedAccount')
			}
		} catch (e) {
			setLoading(false)

			setCheckCount(checkCount + 1)

			if(e.message.toLowerCase() === "invalid credentials"){
				if (checkCount < 4) {
					setErrorMessage(`Invalid OTP, ${4 - checkCount} more consecutive failed attempts will lock the account`)
					return setError('otp', {
						type: "server",
						message: `Invalid OTP, ${4 - checkCount} more consecutive failed attempts will lock the account`
					})
				} else {
					const now = Date.parse(new Date())
					localStorage.setItem("lockedTime", now)
					localStorage.setItem("lockedAccount", user?.email)
					setLockedAccount(now)
					setErrorMessage(`Account locked, please retry after sometime`)
					return setError('otp', {
						type: "server",
						message: "Account locked, pls retry after  sometime"
					})
				}
			}
		}
	}

	/*=================================================
		Additional Function
	/================================================*/
	function checkTime() {
		if (lockedAccount && (localStorage.getItem("lockedAccount") === user?.email)) {
			return Date.parse(new Date()) > (lockedAccount + 15 * 60 * 1000)
		}

		return true
	}

	/*=================================================
		Navigate
	/================================================*/
	function goToResetTfa() {
		goTo(`/reset-2fa`)
	}

	/*=================================================
		MultiDex
	/================================================*/
	const { dex } = getEnvAndDex();
	const { dexName } = getDexConfig(dex);

	return (
		<div className='layout-login'>
			<DexLeftComponent />

			<div className='layout-login__right layout-login__right--grey'>
				<div className='set-up-tfa'>
					<div className='set-up-tfa__title--header-1'>Activate <br /> Two Factor Authentication</div>
					<div className='set-up-tfa__sub-header'>Scan the below QR Code or Input the Secret Key in Google Authenticator or Microsoft Authenticator Mobile App and generate a Security Code.</div>
					<div className='set-up-tfa__content'>
						<div className="set-up-tfa__content__top">
							<div className="set-up-tfa__content__top__left">
								<span className='set-up-tfa__label'>QR Code</span>
								<div className="set-up-tfa__content__qr-image--outer">
									<span className='set-up-tfa__content__qr-image-inner'>
										<QRCodeSVG
											value={`otpauth://totp/${dexName}:${user?.email}?secret=${currentItemTwoFactorAuthentication?.secret}&issuer=${dexName}`}
											size={105}
											bgColor={"#ffffff"}
											fgColor={"#000000"}
											level={"L"}
											includeMargin={false}
										/>
									</span>
								</div>
							</div>
							<div className="set-up-tfa__content__top__right">
								<span className="set-up-tfa__label">Secret Key</span>
								<div className='set-up-tfa__content__secret'>
									<Input
										defaultValue={currentItemTwoFactorAuthentication?.secret}
										type={customMask ? 'password' : 'text'}
										disabled={true}
										data-testid="secret-input"
										placeholder="1234567890"
									/>
									<span data-testid="reveal-btn" className="link icon-eye" onClick={() => setCustomMask(!customMask)}>
										<IconEye bool={customMask} />
									</span>
								</div>
							</div>
						</div>
						<div className="set-up-tfa__content__bottom">
							<form id="tfaForm" onSubmit={handleSubmit(formHandler)}>
								{errorMessage && (
									<div className='login-box__form__col--error'>
										<Error /> <span className='t--required'>{errorMessage}</span>
									</div>
								)}
								<span className="set-up-tfa__label">Input Security Code Generated by the Authenticator App</span>
								<Input
									maxWidth="360"
									name="otp"
									placeholder="0-0-0-0-0-0"
									register={register({
										required: "This field is mandatory",
										minLength: {
											value: 6,
											message: "Please enter a valid OTP"
										}
									})}
									error={errors.otp}
									maxLength={6}
									type="number"
									data-testid="otp-input"
								/>
								<div className="set-up-tfa__content__action__top">
									<span className="link" onClick={() => goToResetTfa()}>Trouble with Security Code?</span>
								</div>
							</form>
						</div>
						<div className="set-up-tfa__content__action">
							<div className="set-up-tfa__content__action__top">
								<Button
									type='submit'
									fullWidth style={{ maxWidth: '168px' }} value='Submit'
									id="verify-activate-submit-id"
									disabled={
										checkTime() ? !isValid : true
									}
									loading={loading}
									iconOnly={loading}
									aria-label={loading ? 'Verifying' : undefined}
									form={"tfaForm"}
								>Verify & Activate</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
export default Page
